import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Check, UserPlus, Lock, Trash2, BellRing, NotebookPen } from "lucide-react";
import { cn } from "@/lib/utils";
import { API, formatLongDate, formatShortDate, studentLabel, lessonTitle, lessonRecord } from "@/lib/attendance";
import GuestPicker from "@/components/attendance/GuestPicker";

export default function AttendanceSheet({ token, date, lesson, editable, isFuture, onClose, onSaved, onAuthError }) {
  const rec = lessonRecord(lesson);
  const records = lesson.parts.map((p) => p.record).filter(Boolean);
  const students = lesson.parts.flatMap((p) => p.students);
  const reminders = lesson.parts.map((p) => p.reminder).filter(Boolean)
    .filter((r, i, arr) => arr.findIndex((x) => x.next_note === r.next_note) === i);
  const [status, setStatus] = useState(rec?.status || (isFuture ? "postponed" : "held"));
  const [present, setPresent] = useState(() => new Set(records.flatMap((r) => r.present_ids)));
  const [guests, setGuests] = useState(() => records.flatMap((r) => r.guests));
  const [reason, setReason] = useState(rec?.postpone_reason || "");
  const [needsRecovery, setNeedsRecovery] = useState(rec ? rec.needs_recovery : true);
  const [lessonNote, setLessonNote] = useState(rec?.lesson_note || "");
  const [nextNote, setNextNote] = useState(rec?.next_note || "");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const locked = !editable;

  const toggle = (id) => {
    if (locked) return;
    setPresent((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Shared athletic slot: one register per group, guests stored on the first group
      await Promise.all(lesson.parts.map((p, i) => axios.put(`${API}/teacher/attendance`, {
        date, group_id: p.group_id, start_time: lesson.start_time, court_id: lesson.court_id,
        status, present_ids: p.students.map((s) => s.id).filter((id) => present.has(id)),
        guests: i === 0 ? guests : [], postpone_reason: reason, needs_recovery: needsRecovery,
        lesson_note: lessonNote, next_note: nextNote,
      }, { headers: { Authorization: `Bearer ${token}` } })));
      toast.success(status === "held" ? "Presenze salvate" : "Lezione segnata come rimandata");
      onSaved();
    } catch (err) {
      if (err?.response?.status === 401) onAuthError(err);
      else toast.error(err?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const presentCount = present.size + guests.filter((g) => g.present).length;
  const canSave = !locked && (status === "held" ? !isFuture : reason.trim().length > 0);

  return (
    <div className="fixed inset-0 z-50 bg-slate-50 flex flex-col" data-testid="attendance-sheet">
      <div className="bg-white border-b border-slate-200 px-4 py-3" style={{ borderTop: `6px solid ${lesson.parts[0].color}` }}>
        <div className="max-w-2xl mx-auto flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xl font-bold text-slate-900" data-testid="sheet-group-name">
              {lessonTitle(lesson)}
            </div>
            <div className="text-sm text-slate-500 capitalize">
              {formatLongDate(date)} · <span className="font-mono">{lesson.start_time}–{lesson.end_time}</span> · {lesson.court_nome}
            </div>
          </div>
          <Button variant="outline" onClick={onClose} className="h-11 w-11 p-0 shrink-0" data-testid="sheet-close-btn" aria-label="Chiudi">
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="max-w-2xl mx-auto grid grid-cols-2 gap-2 mt-3">
          <ModeButton active={status === "held"} disabled={locked} onClick={() => setStatus("held")} testid="mode-held-btn">Lezione svolta</ModeButton>
          <ModeButton active={status === "postponed"} disabled={locked} onClick={() => setStatus("postponed")} testid="mode-postponed-btn">Rimandata</ModeButton>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">
          {locked && (
            <div className="rounded-xl bg-slate-200 text-slate-700 px-4 py-3 text-sm flex gap-2" data-testid="sheet-locked-banner">
              <Lock className="w-4 h-4 mt-0.5" /> Registro chiuso, non più modificabile.
            </div>
          )}
          {reminders.map((r, i) => <ReminderBox key={i} reminder={r} />)}
          {status === "held" ? (
            <HeldSection {...{ lesson, students, present, toggle, guests, setGuests, locked, isFuture, presentCount }} onAddGuest={() => setPickerOpen(true)} />
          ) : (
            <PostponedSection {...{ reason, setReason, needsRecovery, setNeedsRecovery, locked }} />
          )}
          <NotesSection {...{ lessonNote, setLessonNote, nextNote, setNextNote, locked }} />
          {rec && (
            <div className="text-xs text-slate-400 text-center pt-2" data-testid="sheet-last-edit">
              Ultima modifica: <span className="capitalize">{rec.teacher}</span>, {new Date(rec.updated_at).toLocaleString("it-IT")}
            </div>
          )}
        </div>
      </div>

      {!locked && (
        <div className="bg-white border-t border-slate-200 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="max-w-2xl mx-auto">
            <Button onClick={save} disabled={!canSave || saving} data-testid="sheet-save-btn"
              className="w-full h-14 text-lg font-bold bg-[#15803D] hover:bg-[#166534]">
              {saving ? "Salvataggio..." : status === "held" ? `Salva presenze (${presentCount})` : "Salva rinvio"}
            </Button>
          </div>
        </div>
      )}

      {pickerOpen && (
        <GuestPicker token={token} excludeIds={[...students.map((s) => s.id), ...guests.map((g) => g.registration_id).filter(Boolean)]}
          onClose={() => setPickerOpen(false)}
          onAdd={(g) => { setGuests((prev) => [...prev, { ...g, present: true }]); setPickerOpen(false); }}
          onAuthError={onAuthError} />
      )}
    </div>
  );
}

function ModeButton({ active, disabled, onClick, testid, children }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={cn("h-12 rounded-xl font-semibold text-sm border-2 transition-colors",
        active ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-600",
        disabled && "opacity-60")}>
      {children}
    </button>
  );
}

function HeldSection({ lesson, students, present, toggle, guests, setGuests, locked, isFuture, presentCount, onAddGuest }) {
  const optional = new Set(lesson.parts.flatMap((p) => p.optional_ids || []));
  const expected = students.filter((s) => !optional.has(s.id));
  const absent = expected.filter((s) => !present.has(s.id)).length + guests.filter((g) => !g.present).length;
  const multi = lesson.parts.length > 1;
  const extras = students.filter((s) => optional.has(s.id));
  return (
    <>
      {isFuture && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 text-sm" data-testid="future-warning">
          Questa lezione è nel futuro: potrai segnare le presenze il giorno stesso. Puoi però segnarla come rimandata.
        </div>
      )}
      <div className="grid grid-cols-2 gap-2" data-testid="attendance-summary">
        <div className="rounded-xl bg-green-100 text-green-900 px-4 py-3">
          <div className="text-2xl font-extrabold" data-testid="present-count">{presentCount}</div>
          <div className="text-xs font-semibold uppercase tracking-wider">Presenti</div>
        </div>
        <div className="rounded-xl bg-slate-200 text-slate-700 px-4 py-3">
          <div className="text-2xl font-extrabold" data-testid="absent-count">{absent}</div>
          <div className="text-xs font-semibold uppercase tracking-wider">Assenti</div>
        </div>
      </div>
      <p className="text-xs text-slate-500">Tocca il nome di chi è presente: diventa verde.</p>
      <div className="space-y-2" data-testid="students-list">
        {lesson.parts.map((p) => (
          <div key={p.group_id} className="space-y-2" data-testid={`part-${p.group_id}`}>
            {multi && (
              <div className="pt-2 text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} /> {p.group_nome}
              </div>
            )}
            {p.students.filter((s) => !optional.has(s.id)).map((s) => (
              <StudentRow key={s.id} label={studentLabel(s)} on={present.has(s.id)} disabled={locked || isFuture}
                onClick={() => toggle(s.id)} testid={`student-row-${s.id}`} />
            ))}
          </div>
        ))}
        {!students.length && <div className="text-sm text-slate-500 py-2">Nessun allievo iscritto al gruppo.</div>}
        {guests.map((g, i) => (
          <StudentRow key={`g-${i}`} label={studentLabel(g)} on={g.present} guest external={!g.registration_id}
            disabled={locked || isFuture} testid={`guest-row-${i}`}
            onClick={() => setGuests((prev) => prev.map((x, j) => (j === i ? { ...x, present: !x.present } : x)))}
            onRemove={locked ? null : () => setGuests((prev) => prev.filter((_, j) => j !== i))} />
        ))}
        {extras.length > 0 && (
          <div className="space-y-2 pt-3" data-testid="optional-students">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Non previsti oggi · non contano come assenti</div>
            {extras.map((s) => (
              <StudentRow key={s.id} label={studentLabel(s)} on={present.has(s.id)} disabled={locked || isFuture} muted
                onClick={() => toggle(s.id)} testid={`optional-row-${s.id}`} />
            ))}
          </div>
        )}
      </div>
      {!locked && !isFuture && (
        <Button variant="outline" onClick={onAddGuest} className="w-full h-12 gap-2 border-dashed border-2 text-[#C85A32] border-[#C85A32]/40" data-testid="add-guest-btn">
          <UserPlus className="w-5 h-5" /> Aggiungi allievo ospite
        </Button>
      )}
    </>
  );
}

function StudentRow({ label, on, guest, external, muted, disabled, onClick, onRemove, testid }) {
  return (
    <div className="flex gap-2">
      <button onClick={onClick} disabled={disabled} data-testid={testid} data-present={on ? "true" : "false"}
        className={cn("flex-1 min-h-[56px] rounded-xl px-4 py-3 flex items-center gap-3 text-left border-2 transition-colors",
          on ? "bg-green-700 border-green-700 text-white" : muted ? "bg-slate-100 border-dashed border-slate-300 text-slate-500" : "bg-white border-slate-200 text-slate-800")}>
        <span className={cn("w-7 h-7 rounded-full flex items-center justify-center shrink-0 border-2", on ? "bg-white border-white" : "border-slate-300")}>
          {on && <Check className="w-4 h-4 text-green-700" strokeWidth={3} />}
        </span>
        <span className="flex-1 text-base font-semibold">{label}</span>
        {guest && (
          <span className={cn("text-[10px] font-bold uppercase rounded-full px-2 py-0.5", on ? "bg-white/25 text-white" : "bg-orange-100 text-orange-800")}>
            {external ? "Esterno" : "Ospite"}
          </span>
        )}
      </button>
      {onRemove && (
        <Button variant="outline" onClick={onRemove} className="h-auto w-12 p-0 shrink-0" aria-label="Rimuovi ospite" data-testid={`${testid}-remove`}>
          <Trash2 className="w-4 h-4 text-slate-500" />
        </Button>
      )}
    </div>
  );
}

function ReminderBox({ reminder }) {
  return (
    <div className="rounded-xl bg-amber-50 border-2 border-amber-300 px-4 py-3" data-testid="previous-reminder">
      <div className="text-xs font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1.5">
        <BellRing className="w-4 h-4 shrink-0" />
        <span>Da ricordare · lezione del {formatShortDate(reminder.date)} ({reminder.teacher})</span>
      </div>
      <p className="mt-1 text-base text-slate-900 whitespace-pre-wrap" data-testid="previous-reminder-text">{reminder.next_note}</p>
    </div>
  );
}

function NotesSection({ lessonNote, setLessonNote, nextNote, setNextNote, locked }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4" data-testid="notes-section">
      <div className="space-y-1.5">
        <label htmlFor="lesson-note" className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
          <NotebookPen className="w-4 h-4" /> Note sulla lezione
        </label>
        <Textarea id="lesson-note" value={lessonNote} onChange={(e) => setLessonNote(e.target.value)} disabled={locked}
          placeholder="Programma svolto, considerazioni, appunti..." className="text-base min-h-[96px]" data-testid="lesson-note-input" />
      </div>
      <div className="space-y-1.5">
        <label htmlFor="next-note" className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
          <BellRing className="w-4 h-4" /> Da ricordare la prossima lezione
        </label>
        <Textarea id="next-note" value={nextNote} onChange={(e) => setNextNote(e.target.value)} disabled={locked}
          placeholder="Cose da fare o da ricordare..." className="text-base min-h-[80px]" data-testid="next-note-input" />
        <p className="text-xs text-slate-500">Comparirà in cima alla prossima lezione di questo gruppo.</p>
      </div>
    </div>
  );
}

function PostponedSection({ reason, setReason, needsRecovery, setNeedsRecovery, locked }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4">
      <div className="space-y-1.5">
        <label htmlFor="postpone-reason" className="text-sm font-semibold text-slate-800">Motivo del rinvio *</label>
        <Textarea id="postpone-reason" value={reason} onChange={(e) => setReason(e.target.value)} disabled={locked}
          placeholder="Es. pioggia, campo impraticabile, maestro malato..." className="text-base min-h-[96px]" data-testid="postpone-reason-input" />
      </div>
      <label className="flex items-center gap-3 min-h-[48px] cursor-pointer">
        <Checkbox checked={needsRecovery} onCheckedChange={(v) => setNeedsRecovery(!!v)} disabled={locked}
          className="w-6 h-6" data-testid="needs-recovery-checkbox" />
        <span className="text-base font-medium text-slate-800">La lezione va recuperata</span>
      </label>
    </div>
  );
}
